import type { BadgeTone } from '@/components/Badge';
import type { DokladStrana, DokladTyp, Ridic } from './driver-api';

export const DOKLAD_TYP_LABEL: Record<DokladTyp, string> = {
  op: 'Občanský průkaz',
  rp: 'Řidičský průkaz',
  pas: 'Cestovní pas',
};

export const DOKLAD_TYPY: DokladTyp[] = ['op', 'rp', 'pas'];

export const DOKLAD_STRANA_LABEL: Record<DokladStrana, string> = {
  predni: 'Přední strana',
  zadni: 'Zadní strana',
};

/** Passports have a single page; the other two documents carry both sides. */
export function dokladHasBack(typ: DokladTyp): boolean {
  return typ !== 'pas';
}

export type DokladStatus = 'platny' | 'konci' | 'prosly' | 'neznamy';

/** Days from `today` to the expiry, ≤ 30 counts as "ending soon". */
export const DOKLAD_KONCI_DNY = 30;

export function dokladStatus(datumPlatnosti: string | null, today: Date = new Date()): DokladStatus {
  if (!datumPlatnosti) return 'neznamy';
  const expiry = new Date(`${datumPlatnosti.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(expiry.getTime())) return 'neznamy';
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((expiry.getTime() - start.getTime()) / 86_400_000);
  if (days < 0) return 'prosly';
  if (days <= DOKLAD_KONCI_DNY) return 'konci';
  return 'platny';
}

export const DOKLAD_STATUS_LABEL: Record<DokladStatus, string> = {
  platny: 'Platný',
  konci: 'Končí platnost',
  prosly: 'Prošlý',
  neznamy: 'Bez platnosti',
};

export const DOKLAD_STATUS_TONE: Record<DokladStatus, BadgeTone> = {
  platny: 'ok',
  konci: 'warn',
  prosly: 'danger',
  neznamy: 'neutral',
};

export function ridicFullName(r: Pick<Ridic, 'jmeno' | 'prijmeni'>): string {
  return [r.jmeno, r.prijmeni].filter(Boolean).join(' ').trim();
}

/**
 * "Zákazník je řidič" prefill: a private customer's `jmeno` holds the person's
 * name, so the last word is the příjmení. A single word lands in příjmení.
 */
export function splitPersonName(full: string): { jmeno: string; prijmeni: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { jmeno: '', prijmeni: '' };
  if (parts.length === 1) return { jmeno: '', prijmeni: parts[0] };
  return { jmeno: parts.slice(0, -1).join(' '), prijmeni: parts[parts.length - 1] };
}
